import { useMemo } from "react";
import type { FormErrors } from "redux-form";
import { SubmissionError } from "redux-form";

import { fetchCurrentUser } from "@js/apps/auth/actions";
import { fetchFreelancerProfile } from "@js/apps/freelancer/actions";
import type { AccountInfoFormData } from "@js/apps/freelancer/forms/account-info";
import {
  prepareExternalProfilesError,
  prepareUpdateFreelancerCustomizationExternalProfilesValue,
} from "@js/apps/freelancer/hooks/account-info/utils";
import { useAppDispatch } from "@js/hooks";
import type { Freelancer } from "@js/types/freelancer";
import { typeGuard } from "@js/utils";

import {
  useGetFreelancerExternalProfilesQuery,
  useLazyGetFreelancerProfileCompletionQuery,
  useUpdateFreelancerCustomizationMutation,
} from "../../api";

export const useAccountInfo = (profile: Freelancer | undefined) => {
  const dispatch = useAppDispatch();

  const [getFreelancerProfileCompletion] =
    useLazyGetFreelancerProfileCompletionQuery();
  const { data: externalProfiles, isLoading } =
    useGetFreelancerExternalProfilesQuery();

  const [updateFreelancerCustomization] =
    useUpdateFreelancerCustomizationMutation();

  const initialValues = useMemo<AccountInfoFormData>(
    () => ({
      user: {
        first_name: profile?.user?.first_name || "",
        last_name: profile?.user?.last_name || "",
      },
      external_profiles: externalProfiles,
      location_full: profile?.location_full || null,
    }),
    [
      externalProfiles,
      profile?.location_full,
      profile?.user.first_name,
      profile?.user.last_name,
    ],
  );

  const onSubmit = async (values: AccountInfoFormData) => {
    if (!profile) return;

    const preparedExternalProfiles =
      prepareUpdateFreelancerCustomizationExternalProfilesValue(values);

    try {
      await updateFreelancerCustomization({
        id: profile.id,
        data: {
          external_profiles: preparedExternalProfiles,
          user: {
            first_name: values.user.first_name,
            last_name: values.user.last_name,
          },
          freelancer: {
            location_full: values.location_full,
          },
        },
      }).unwrap();
      void getFreelancerProfileCompletion({
        freelancerId: profile.id,
      })
        .unwrap()
        .catch(() => null);
      void dispatch(fetchFreelancerProfile(profile.id));
    } catch (error: unknown) {
      throw new SubmissionError(
        prepareExternalProfilesError({
          externalProfiles,
          errorData:
            error && typeGuard<unknown, { data: unknown }>(error, "data")
              ? error.data
              : {},
          preparedFormValue: preparedExternalProfiles,
        }) as FormErrors,
      );
    }
  };

  const onSubmitSuccess = () => {
    if (!profile) return;
    dispatch(fetchCurrentUser()).catch(() => null);
  };

  return {
    onSubmit,
    onSubmitSuccess,
    initialValues,
    isLoading,
  };
};
